import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { ChevronRight, Briefcase, Building2 } from "lucide-react";

/**
 * Dynamic breadcrumb for commercialisti: shows
 *   Studio › [Active Client] › [Section]
 *
 * Auto-hidden for non-commercialisti and for the top-level /app dashboard
 * (which already has its own "Studio commercialista" hero). The active
 * client is derived from GET /api/company (server-side scoped to the
 * active company_id).
 */
const SECTION_LABELS = [
  { match: (p) => p === "/app", label: null }, // hero handles it
  { match: (p) => p === "/app/invoices", label: "Fatture" },
  { match: (p) => p.startsWith("/app/invoices/"), label: "Dettaglio fattura" },
  { match: (p) => p === "/app/upload", label: "Carica PDF" },
  { match: (p) => p === "/app/company", label: "Profilo cliente" },
  { match: (p) => p === "/app/suppliers", label: "Rubrica fornitori" },
  { match: (p) => p === "/app/integrations", label: "Integrazioni" },
];

const STUDIO_SECTIONS = [
  { match: (p) => p === "/app/account", label: "Dati profilo" },
  { match: (p) => p === "/app/account/credits", label: "Crediti e piani" },
  { match: (p) => p === "/app/account/password", label: "Cambio password" },
  { match: (p) => p === "/app/account/companies", label: "Aziende" },
  { match: (p) => p === "/app/account/referral", label: "Referral" },
  { match: (p) => p === "/app/account/reviews", label: "Recensioni" },
];

export default function CommercialistaBreadcrumbs() {
  const location = useLocation();
  const [activeClient, setActiveClient] = useState(null);
  const isStudio = location.pathname.startsWith("/app/account");

  useEffect(() => {
    let cancelled = false;
    if (isStudio) { setActiveClient(null); return; }
    api.get("/company")
      .then(({ data }) => { if (!cancelled) setActiveClient(data?.denominazione || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname, isStudio]);

  // Determine section label
  const list = isStudio ? STUDIO_SECTIONS : SECTION_LABELS;
  const section = list.find((s) => s.match(location.pathname));
  if (!section || !section.label) return null; // no breadcrumb for dashboard root

  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="commercialista-breadcrumbs"
      className="px-4 sm:px-6 lg:px-10 pt-4 pb-1 text-xs"
    >
      <ol className="flex flex-wrap items-center gap-1 text-[#737373]">
        <li className="inline-flex items-center gap-1">
          <Briefcase className="w-3 h-3 text-[#6D28D9]" />
          <span className="font-semibold text-[#6D28D9]">Studio</span>
        </li>
        {!isStudio && (
          <>
            <li><ChevronRight className="w-3 h-3 text-[#A3A3A3]" /></li>
            <li className="inline-flex items-center gap-1">
              <Building2 className="w-3 h-3 text-[#1D4ED8]" />
              {activeClient ? (
                <Link to="/app" className="font-semibold text-[#1D4ED8] hover:underline truncate max-w-[220px]" title={activeClient}>
                  {activeClient}
                </Link>
              ) : (
                <span className="text-[#A3A3A3] italic">Nessun cliente attivo</span>
              )}
            </li>
          </>
        )}
        <li><ChevronRight className="w-3 h-3 text-[#A3A3A3]" /></li>
        <li className="text-[#0A0A0A] font-semibold" aria-current="page">{section.label}</li>
      </ol>
    </nav>
  );
}
